import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X, LogOut, LayoutDashboard, ChevronDown } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

export default function Navbar() {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const nav = useNavigate();
  const { customer, logout } = useCustomerAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Session 2 · Punkt 9: Hintergrund-Scroll sperren, wenn Mobile-Menü offen ist
  useEffect(() => {
    if (open) {
      const prevOverflow = document.body.style.overflow;
      const prevPaddingRight = document.body.style.paddingRight;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
      return () => {
        document.body.style.overflow = prevOverflow;
        document.body.style.paddingRight = prevPaddingRight;
      };
    }
  }, [open]);

  // Menü automatisch schließen, wenn Route wechselt
  useEffect(() => {
    setOpen(false);
  }, [pathname, search]);

  // Business context: user is on /businesskunden or already in business inquiry
  const isBusinessContext =
    pathname.startsWith("/businesskunden") ||
    (pathname === "/buchen" && search.includes("type=business"));
  const ctaTo = isBusinessContext ? "/buchen?type=business" : "/buchen";

  const ALL_LINKS = [
    { to: "/privatkunden", label: t("nav.private") },
    { to: "/businesskunden", label: t("nav.business") },
    { to: "/produkte", label: t("nav.products") },
    { to: "/layouts", label: t("nav.layouts") },
    { to: "/kundenkonto", label: t("nav.account") },
  ];
  // In business context, keep only the essentials
  const BUSINESS_ALLOWED = new Set(["/privatkunden", "/businesskunden", "/layouts", "/kundenkonto"]);
  const LINKS = isBusinessContext
    ? ALL_LINKS.filter((l) => BUSINESS_ALLOWED.has(l.to))
    : ALL_LINKS;

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-700 ${
          scrolled || open
            ? "bg-ink-950/90 backdrop-blur-xl border-b border-cream/[0.06] py-3"
            : "bg-transparent py-6"
        }`}
      >
        <div className="container-x flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
            <span className="font-display text-2xl md:text-[1.55rem] tracking-tight text-cream">
              Picturebox<span className="text-gold">·</span>24
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-10 xl:gap-12 ml-12 xl:ml-16">
            {LINKS.map((l) => {
              // Session 15.5 · "Mein Konto" bekommt Hover-Dropdown mit "Ausloggen", wenn Kunde eingeloggt ist
              if (l.to === "/kundenkonto" && customer) {
                return (
                  <div
                    key={l.to}
                    className="relative"
                    onMouseEnter={() => setAccountMenuOpen(true)}
                    onMouseLeave={() => setAccountMenuOpen(false)}
                    data-testid="nav-account-dropdown"
                  >
                    <button
                      type="button"
                      onClick={() => nav("/kundenkonto")}
                      className={`text-[0.72rem] uppercase tracking-[0.28em] link-underline transition-colors flex items-center gap-1 ${
                        pathname === "/kundenkonto" ? "text-gold" : "text-cream/70 hover:text-cream"
                      }`}
                      data-testid="nav-kundenkonto"
                    >
                      {l.label}
                      <ChevronDown className="h-3 w-3" />
                    </button>
                    {accountMenuOpen && (
                      <div
                        className="absolute right-0 top-full pt-3 min-w-[220px] z-[65]"
                        data-testid="account-dropdown-panel"
                      >
                        <div className="bg-ink-950/98 backdrop-blur-xl border border-cream/[0.08] shadow-2xl overflow-hidden">
                          <p className="px-4 py-3 border-b border-cream/[0.06] text-[0.65rem] uppercase tracking-[0.22em] text-cream/40 truncate">
                            {customer.email}
                          </p>
                          <Link
                            to="/kundenkonto"
                            onClick={() => setAccountMenuOpen(false)}
                            className="flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.22em] text-cream/80 hover:text-gold hover:bg-cream/[0.03] transition-colors"
                            data-testid="account-dropdown-dashboard"
                          >
                            <LayoutDashboard className="h-3.5 w-3.5" /> Zum Dashboard
                          </Link>
                          <button
                            type="button"
                            onClick={() => {
                              setAccountMenuOpen(false);
                              logout();
                              nav("/");
                            }}
                            className="w-full flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.22em] text-cream/80 hover:text-red-400 hover:bg-cream/[0.03] transition-colors border-t border-cream/[0.06] text-left"
                            data-testid="account-dropdown-logout"
                          >
                            <LogOut className="h-3.5 w-3.5" /> Ausloggen
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.to.slice(1)}`}
                  className={({ isActive }) =>
                    `text-[0.72rem] uppercase tracking-[0.28em] link-underline transition-colors ${
                      isActive ? "text-gold" : "text-cream/70 hover:text-cream"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <LanguageSwitcher />
          </div>

          <button
            className="lg:hidden text-cream p-2 relative z-[70]"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label={t("nav.menu_toggle")}
            aria-expanded={open}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </header>

      {/* Session 2 · Punkt 9: Vollflächiges Mobile-Overlay mit Scroll-Lock & höherem z-Index */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 z-[55] bg-ink-950/95 backdrop-blur-xl overflow-y-auto pt-24 pb-12"
          data-testid="mobile-menu"
          role="dialog"
          aria-modal="true"
        >
          <div className="container-x">
            <div className="flex flex-col gap-6">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `text-base uppercase tracking-[0.25em] py-2 border-b border-cream/[0.06] ${isActive ? "text-gold" : "text-cream/85 hover:text-gold"}`
                  }
                  data-testid={`mobile-nav-${l.to.slice(1)}`}
                >
                  {l.label}
                </NavLink>
              ))}
              {customer && (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    logout();
                    nav("/");
                  }}
                  data-testid="mobile-nav-logout"
                  className="text-base uppercase tracking-[0.25em] py-2 border-b border-cream/[0.06] text-red-400/90 hover:text-red-400 flex items-center gap-2 text-left"
                >
                  <LogOut className="h-4 w-4" /> Ausloggen ({customer.email})
                </button>
              )}
              <div className="pt-3">
                <LanguageSwitcher />
              </div>
              <Link
                to={ctaTo}
                onClick={() => setOpen(false)}
                className="btn-gold mt-2 text-center"
                data-testid="cta-book-mobile"
              >
                {t("nav.cta_book")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
